/**
 * WEB-3767, скрины владельца 30.10 пункт 9 («не наша галочка, все нужно по киту
 * довести все алерты всплывающие»; скрин — «Блок скопирован — копия под
 * исходным» с зелёной галочкой antd): все всплывашки кабинета идут одним общим
 * путём — тостом кита (`useToast`, эталон `kit.jsx:1122–1137`), без правки
 * каждого места вызова (их больше полутора сотен в восьмидесяти файлах).
 *
 * Источников всплывашек antd три: `App.useApp().message` (корень `<AntApp>` в
 * `App.tsx`), статический `message` из `antd` и экземпляр `message.useMessage()`
 * (фасад редактора), плюс вложенные `<App>` (окна и страницы со своим
 * контекстом стилей). `KitMessageHost` у корня держит тост кита в своём корне
 * `.ds` с темой человека (класс `toast-host`, как `ShellToastHost`) и, пока
 * смонтирован, подменяет у корневого и статического `message` методы
 * `success / error / warning / info`: вызов кладёт строку в тост кита
 * (`success` → `ok`, `error` → `err`, `warning` → `warn`, `info` → `info`), срок
 * antd в секундах переносится. Экземпляры ниже корня подменяет
 * `useKitMessagePatch(api)` / `<KitMessagePatch />` — только под узлом: без него
 * (тесты и витрины без корня) методы antd прежние. Содержимое не строкой (узел
 * React) идёт прежним методом antd — в тост кита кладётся только строка.
 *
 * Граница: вызов из эффекта монтирования ребёнка срабатывает раньше подмены у
 * корня (эффекты родителя идут после детей) и уходит в antd; таких вызовов в
 * кабинете нет (сверено поиском 30.10).
 */
import React, {
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { App as AntApp, message as staticMessage } from 'antd';
import { useToast, type ToastKind } from '@/components/kit/ds';
import { useAppSelector } from '@/hooks/hooks';
import { useCabinetRootAttributes } from '@/theme/cabinetTokens';
import type { UserTheme } from '@/types';

export type KitShow = (text: string, kind: ToastKind, ms?: number) => void;
/** Погасить тост: с ключом — только показанный под этим ключом, без — любой. */
export type KitHide = (key?: string) => void;
interface KitToastControl {
  show: (text: string, kind: ToastKind, ms?: number, key?: string) => void;
  hide: KitHide;
}

const KIND = { success: 'ok', error: 'err', warning: 'warn', info: 'info' } as const;
type Method = keyof typeof KIND;
const METHODS = Object.keys(KIND) as Method[];

type Fn = (...args: unknown[]) => unknown;

const KitShowContext = createContext<KitToastControl | null>(null);

/** Срок antd (секунды; 0 и пусто — срок кита). */
function seconds(v: unknown): number | undefined {
  return typeof v === 'number' && v > 0 ? v * 1000 : undefined;
}

/** Строка и срок вызова antd: `(текст, срок)` или `({ content, duration })`. */
function readCall(args: unknown[]): { text: string; ms?: number; key?: string } | null {
  const [a, b] = args;
  if (typeof a === 'string' || typeof a === 'number') return { text: String(a), ms: seconds(b) };
  if (a && typeof a === 'object' && !isValidElement(a) && 'content' in a) {
    const { content, duration, key } = a as { content?: unknown; duration?: unknown; key?: unknown };
    if (typeof content === 'string' || typeof content === 'number')
      return {
        text: String(content),
        ms: seconds(duration),
        key: typeof key === 'string' || typeof key === 'number' ? String(key) : undefined,
      };
  }
  return null;
}

/** Ответ вызова, как у antd: закрывашка с `then` (`await message.success(…)`). */
function done(): (() => void) & PromiseLike<boolean> {
  const close = () => undefined;
  return Object.assign(close, {
    then: <R1, R2>(ok?: (v: boolean) => R1 | PromiseLike<R1>, bad?: (e: unknown) => R2 | PromiseLike<R2>) =>
      Promise.resolve(true).then(ok, bad),
  });
}

/**
 * Подменить методы всплывашек у `api`; вернуть функцию, снимающую подмену.
 * `destroy(ключ)` гасит и тост кита (WEB-832 п. 3: после удачного сохранения
 * предупреждение и ошибка сохранения гаснут сразу), и прежние всплывашки antd.
 */
export function patchMessageApi(
  api: object,
  show: KitToastControl['show'] | KitShow,
  hide?: KitHide,
): () => void {
  const target = api as Partial<Record<Method | 'destroy', Fn>>;
  const saved = [...METHODS, 'destroy' as const].map((m) => [m, target[m]] as const);
  for (const [m, original] of saved) {
    if (m === 'destroy') {
      target.destroy = (...args: unknown[]) => {
        const [key] = args;
        hide?.(typeof key === 'string' || typeof key === 'number' ? String(key) : undefined);
        return original?.apply(api, args);
      };
      continue;
    }
    target[m] = (...args: unknown[]) => {
      const call = readCall(args);
      if (!call) return original?.apply(api, args);
      if (call.key === undefined) show(call.text, KIND[m], call.ms);
      else (show as KitToastControl['show'])(call.text, KIND[m], call.ms, call.key);
      return done();
    };
  }
  return () => {
    for (const [m, original] of saved) {
      if (original === undefined) delete target[m];
      else target[m] = original;
    }
  };
}

/** Подменить экземпляр всплывашек antd ниже корня; без `KitMessageHost` — ничего. */
export function useKitMessagePatch(api: object | undefined): void {
  const ctl = useContext(KitShowContext);
  useLayoutEffect(
    () => (ctl && api ? patchMessageApi(api, ctl.show, ctl.hide) : undefined),
    [ctl, api],
  );
}

/** Для вложенного `<App>` antd: его `App.useApp().message` — тоже тост кита. */
export function KitMessagePatch(): null {
  const { message } = AntApp.useApp();
  useKitMessagePatch(message);
  return null;
}

export function KitMessageHost({ children }: { children: React.ReactNode }) {
  const theme = useAppSelector(
    (state) =>
      (state as { auth?: { user?: { theme?: UserTheme } | null } }).auth?.user?.theme,
  );
  const root = useCabinetRootAttributes(theme);
  const [toast, showToast] = useToast();
  // ключ показанного тоста (antd `key`) и признак «погашен destroy» до следующего показа
  const keyRef = useRef<string | undefined>(undefined);
  const [hidden, setHidden] = useState(false);
  const show = useCallback(
    (text: string, kind: ToastKind, ms?: number, key?: string) => {
      keyRef.current = key;
      setHidden(false);
      showToast(text, kind, ms);
    },
    [showToast],
  );
  const hide = useCallback<KitHide>((key) => {
    if (key === undefined || key === keyRef.current) setHidden(true);
  }, []);
  const ctl = useMemo(() => ({ show, hide }), [show, hide]);
  const { message: appMessage } = AntApp.useApp();
  useLayoutEffect(() => patchMessageApi(staticMessage, show, hide), [show, hide]);
  useLayoutEffect(() => patchMessageApi(appMessage, show, hide), [appMessage, show, hide]);
  return (
    <KitShowContext.Provider value={ctl}>
      {children}
      {toast && !hidden && (
        <div {...root} className={`${root.className} toast-host`} data-testid="kit-message-host">
          {toast}
        </div>
      )}
    </KitShowContext.Provider>
  );
}
